/* eslint-disable import/prefer-default-export */
/**
 * 半角カタカナを全角カタカナにする
 * @param str 文字列
 */
export const narrowToWideKana = (str: string): string => {
  const convertMap = {
    ｧ: "ァ",
    ｨ: "ィ",
    ｩ: "ゥ",
    ｪ: "ェ",
    ｫ: "ォ",
    ｬ: "ャ",
    ｭ: "ュ",
    ｮ: "ョ",
    ｯ: "ッ",
    ｰ: "ー",
    ｱ: "ア",
    ｲ: "イ",
    ｳ: "ウ",
    ｴ: "エ",
    ｵ: "オ",
    ｶ: "カ",
    ｷ: "キ",
    ｸ: "ク",
    ｹ: "ケ",
    ｺ: "コ",
    ｻ: "サ",
    ｼ: "シ",
    ｽ: "ス",
    ｾ: "セ",
    ｿ: "ソ",
    ﾀ: "タ",
    ﾁ: "チ",
    ﾂ: "ツ",
    ﾃ: "テ",
    ﾄ: "ト",
    ﾅ: "ナ",
    ﾆ: "ニ",
    ﾇ: "ヌ",
    ﾈ: "ネ",
    ﾉ: "ノ",
    ﾊ: "ハ",
    ﾋ: "ヒ",
    ﾌ: "フ",
    ﾍ: "ヘ",
    ﾎ: "ホ",
    ﾏ: "マ",
    ﾐ: "ミ",
    ﾑ: "ム",
    ﾒ: "メ",
    ﾓ: "モ",
    ﾔ: "ヤ",
    ﾕ: "ユ",
    ﾖ: "ヨ",
    ﾗ: "ラ",
    ﾘ: "リ",
    ﾙ: "ル",
    ﾚ: "レ",
    ﾛ: "ロ",
    ﾜ: "ワ",
    ﾝ: "ン",
    ｦ: "ヲ",
    ｶﾞ: "ガ",
    ｷﾞ: "ギ",
    ｸﾞ: "グ",
    ｹﾞ: "ゲ",
    ｺﾞ: "ゴ",
    ｻﾞ: "ザ",
    ｼﾞ: "ジ",
    ｽﾞ: "ズ",
    ｾﾞ: "ゼ",
    ｿﾞ: "ゾ",
    ﾀﾞ: "ダ",
    ﾁﾞ: "ヂ",
    ﾂﾞ: "ヅ",
    ﾃﾞ: "デ",
    ﾄﾞ: "ド",
    ﾊﾞ: "バ",
    ﾋﾞ: "ビ",
    ﾌﾞ: "ブ",
    ﾍﾞ: "ベ",
    ﾎﾞ: "ボ",
    ﾊﾟ: "パ",
    ﾋﾟ: "ピ",
    ﾌﾟ: "プ",
    ﾍﾟ: "ペ",
    ﾎﾟ: "ポ",
  };

  // eslint-disable-next-line no-restricted-syntax
  for (const [narrowKana, wideKana] of Object.entries(convertMap)) {
    // eslint-disable-next-line no-param-reassign
    str = str.replace(new RegExp(narrowKana, "g"), wideKana);
  }
  return str;
};

const toHalfWidth = (str: string) => {
  // 全角英数字を半角に変換
  // eslint-disable-next-line no-param-reassign
  str = str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
  return str;
};

/**
 * カタカナをひらがなに変換
 * @param str 文字列
 */
export const toHiragana = (str: string | null | undefined) => {
  if (str === null || str === undefined) return "";
  return narrowToWideKana(toHalfWidth(str.toLowerCase()))
    .normalize("NFKC")
    .replace(/[\u30a1-\u30f6]/g, (match) => {
      const char = match.charCodeAt(0) - 0x60;
      return String.fromCharCode(char);
    });
};

/**
 * ダウンロードURL
 * @param url リンクのURL
 * @param prefix fileの最初に入れる文字列
 */
export const downloadFromUrl = async (url: string, prefix: string) => {
  const link = document.createElement("a");
  const date = new Date();
  link.href = url;
  link.download = `${prefix}${date.toISOString().split(".")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * 空の場合にapiに空の情報を送るのを防ぐため、空のkey,valueは削除しておくための関数
 * @param obj 空のkey, valueを取り除くオブジェクト
 * @param requiredKeys 必須のキー
 */
export const removeEmptyValues = <T extends Record<string, any>, K extends keyof T>(
  obj: T,
  requiredKeys: readonly K[] = []
): Pick<T, K> & Partial<Omit<T, K>> => Object.entries(obj).reduce<Pick<T, K> & Partial<Omit<T, K>>>((acc, [key, value]) => {
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '');

  if (!isEmpty || (requiredKeys as readonly (keyof T)[]).includes(key)) {
    (acc as any)[key as keyof T] = value;
  }
  return acc;
}, {} as any);

/**
 * ASCII、半角ｶﾅかどうかをチェック
 * @param str 文字列
 */
export const checkHalfKanaOnly = (str: string): boolean => /^[\x20-\x7E\uFF61-\uFF9F]+$/.test(str);